"use client";

import { useMemo, useState, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import AppWrapper from "@/shared/ui/components/app-wrapper";
import Button from "@/shared/ui/components/button";
import { SuccessFilledIcon } from "@/shared/ui/icons/success-filled-icon";
import Timer from "@/shared/ui/components/timer";
import MoneyAmount from "@/shared/ui/components/money-amount";
import { BrandLogoIcon } from "@/shared/ui/icons/brand-logo-icon";
import { NextPaymentIcon } from "@/shared/ui/icons/next-payment-icon";
import { LanguageSwitcherButton } from "@/shared/ui/components/language-switcher-button";
import { logInfo } from "@/services/logging/logger";
import { type AppContext } from "@/types";
import * as events from "@/events";
import { browserAgent } from "@/shared/telemetry/newrelic";
import * as lib3ds from "@/lib/3ds";

const onSessionFetch = ({ session }: AppContext) => {
  logInfo(
    "success-screen-new",
    `Redirecting to Merchant's Success URL: ${session.merchant.urls.success}`,
    {
      checkoutId: session.id,
      status: session.status,
    },
  );

  try {
    if (sessionStorage.getItem(lib3ds.successScreenLoggedKey(session.id))) {
      return;
    }
    sessionStorage.setItem(lib3ds.successScreenLoggedKey(session.id), "true");
  } catch {
    // sessionStorage might be blocked
  }

  browserAgent.addPageAction("CHECKOUT_SUCCESS_SCREEN_VIEWED", {
    checkout_id: session.id,
    order_id: session.orderId,
    merchant_id: session.merchant.id,
    checkout_status: session.status,
    had_3ds: lib3ds.had3dsCheckoutSession(session.id),
  });
};

function redirectToMerchantSuccess(ctx: AppContext) {
  const redirectUrl = ctx.session.merchant.urls.success;
  if (redirectUrl === "") return;

  browserAgent.addPageAction("MERCHANT_REDIRECT_INITIATED", {
    checkout_id: ctx.session.id,
    order_id: ctx.session.orderId,
    merchant_id: ctx.session.merchant.id,
    had_3ds: lib3ds.had3dsCheckoutSession(ctx.session.id),
    checkout_status: ctx.session.status,
    redirect_url_domain: (() => {
      try { return new URL(redirectUrl).hostname; } catch { return "unknown"; }
    })(),
  });

  window.location.href = redirectUrl;
}

function RedirectFooter({ ctx }: { ctx: AppContext }) {
  const { t } = useTranslation();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const hasRedirectedRef = useRef(false);

  const performRedirect = useCallback(() => {
    if (hasRedirectedRef.current) return;
    
    hasRedirectedRef.current = true;
    setIsRedirecting(true);
    redirectToMerchantSuccess(ctx);
  }, [ctx]);

  return (
    <Timer timesOutInSeconds={5} onFinished={performRedirect}>
      {(secondsRemaining) => (
        <Button
          disabled={isRedirecting}
          size="large"
          variant="primary"
          onClick={performRedirect}
        >
          {t("message.payment_success.cta", { secondsRemaining })}
        </Button>
      )}
    </Timer>
  );
}

function NewSuccessHeader({
  sendEvent,
}: {
  sendEvent: AppContext["sendEvent"];
}) {
  return (
    <div
      className="flex items-center justify-between border-b border-[#EBEEF4] py-6 px-6 mb-5 shrink-0"
      dir="ltr"
    >
      <BrandLogoIcon />
      <LanguageSwitcherButton
        onSwitch={(targetLang) =>
          sendEvent(events.SWITCH_LANGUAGE_CLICKED, { switch_to: targetLang })
        }
      />
    </div>
  );
}

function getNextPaymentFromSession(ctx: AppContext) {
  const option = ctx.session.paymentOptions.find(
    (o) => o.offerId === ctx.session.selectedPaymentOptionId,
  );
  if (!option) return null;
  const schedule = option.schedulePreview ?? [];
  return schedule[1] ?? null;
}

function NextPaymentCard({ ctx }: { ctx: AppContext }) {
  const { t, i18n } = useTranslation();
  const nextPayment = useMemo(
    () => getNextPaymentFromSession(ctx),
    [ctx],
  );
  const rtl = ctx.rtl;

  if (!nextPayment) return null;

  return (
    <div
      className={`mt-6 w-full rounded-xl border border-gray-200 bg-[#F9FAFB] px-4 py-3 flex items-center gap-3 ${rtl ? "flex-row-reverse" : ""}`}
      dir={rtl ? "rtl" : "ltr"}
    >
      <div className="shrink-0 flex items-center justify-center w-10 h-10 rounded-lg bg-white text-[#9600F1]">
        <NextPaymentIcon />
      </div>
      <div className={`flex-1 min-w-0 text-start ${rtl ? "text-end" : "text-start"}`}>
        <p className="text-xs font-medium text-gray-500">
          {t("message.payment_success_new.next_payment_due")}
        </p>
        <p className="mt-0.5 text-sm font-semibold text-gray-900 flex flex-wrap items-center gap-x-1 gap-y-0">
          <MoneyAmount
            amount={nextPayment.amount.amount}
            currency={nextPayment.amount.currency}
            language={i18n.language}
            className="inline-flex"
          />
          <span className="text-gray-400">·</span>
          <span>{ctx.displayDate(nextPayment.dueDate)}</span>
        </p>
      </div>
    </div>
  );
}

function SuccessScreenNewContent({ ctx }: { ctx: AppContext }) {
  const { t } = useTranslation();
  const rtl = ctx.rtl;

  return (
    <div className="min-h-[calc(100vh-200px)] flex flex-col items-center justify-center px-6 py-8">
      <div className="flex flex-col items-center text-center w-full max-w-md">
        <div className="flex items-center justify-center rounded-full text-white mb-6">
          <SuccessFilledIcon />
        </div>

        <div
          className="text-[28px] font-bold text-[#16181D] leading-10 my-4"
          dir={rtl ? "rtl" : "ltr"}
        >
          {t("message.payment_success_new.title")}
        </div>
        <p
          className="mt-2 text-sm text-[#585B7A] font-medium"
          dir={rtl ? "rtl" : "ltr"}
        >
          {t("message.payment_success_new.description")}
        </p>

        <NextPaymentCard ctx={ctx} />
      </div>
    </div>
  );
}

export const SuccessScreen = () => {
  return (
    <AppWrapper
      onSessionFetch={onSessionFetch}
      bare={true}
      stickyFooter={(ctx) => <RedirectFooter ctx={ctx} />}
    >
      {(ctx) => (
        <>
          <NewSuccessHeader sendEvent={ctx.sendEvent} />
          <SuccessScreenNewContent ctx={ctx} />
        </>
      )}
    </AppWrapper>
  );
};

